import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { 
  TeamMember, 
  TeamMemberDetail, 
  TeamMemberSearchCriteria, 
  TeamMemberApiResponse,
  TeamMemberCreateRequest,
  TeamMemberUpdateRequest,
  TeamMemberFarmaciaCreateRequest,
  TeamMemberFarmaciaUpdateRequest
} from '../models/team-member.model';

@Injectable({
  providedIn: 'root'
})
export class TeamMemberService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/teammember`;

  /**
   * Get team members list with search criteria
   */
  getTeamMembers(criteria: TeamMemberSearchCriteria): Observable<TeamMemberApiResponse> {
    const params = this.buildSearchParams(criteria);
    
    //console.log('TeamMemberService: Fetching team members with criteria:', criteria);
    //console.log('TeamMemberService: API URL:', `${this.baseUrl}?${params.toString()}`);

    return this.http.get<TeamMemberApiResponse>(this.baseUrl, { params }).pipe(
      map(response => {
        //console.log('TeamMemberService: API response received:', response);
        return response;
      }),
      catchError(error => {
        console.error('TeamMemberService: Error fetching team members:', error);
        return of({
          records: [],
          totalSize: 0,
          done: true,
          hasMore: false,
          skip: 0,
          limit: 0,
          orderBy: ''
        });
      })
    );
  }

  /**
   * Get team member detail by ID
   */
  getTeamMemberById(id: string): Observable<TeamMemberDetail> {
    const url = `${this.baseUrl}/${id}`;
    //console.log('TeamMemberService: Fetching team member detail:', url);

    return this.http.get<TeamMemberDetail>(url).pipe(
      map(response => {
        //console.log('TeamMemberService: Team member detail received:', response);
        return response;
      }),
      catchError(error => {
        console.error('TeamMemberService: Error fetching team member detail:', error);
        throw error;
      })
    );
  }

  /**
   * Create a new team member (Hospital users)
   */
  createTeamMember(teamMember: TeamMemberCreateRequest): Observable<TeamMemberDetail> {
    //console.log('TeamMemberService: Creating team member (Hospital):', teamMember);

    return this.http.post<TeamMemberDetail>(this.baseUrl, teamMember).pipe(
      map(response => {
        //console.log('TeamMemberService: Team member created:', response);
        return response;
      }),
      catchError(error => {
        console.error('TeamMemberService: Error creating team member:', error);
        throw error;
      })
    );
  }

  /**
   * Create a new team member (Farmacia users)
   */
  createTeamMemberFarmacia(teamMember: TeamMemberFarmaciaCreateRequest): Observable<TeamMemberDetail> {
    //console.log('TeamMemberService: Creating team member (Farmacia):', teamMember);

    return this.http.post<TeamMemberDetail>(this.baseUrl, teamMember).pipe(
      map(response => {
        //console.log('TeamMemberService: Team member created:', response);
        return response;
      }),
      catchError(error => {
        console.error('TeamMemberService: Error creating team member:', error);
        throw error;
      })
    );
  }

  /**
   * Update an existing team member (Hospital users)
   */
  updateTeamMember(id: string, teamMember: TeamMemberUpdateRequest): Observable<TeamMemberDetail> {
    const url = `${this.baseUrl}/${id}`;
    //console.log('TeamMemberService: Updating team member (Hospital):', url, teamMember);

    return this.http.put<TeamMemberDetail>(url, teamMember).pipe(
      map(response => {
        //console.log('TeamMemberService: Team member updated:', response);
        return response;
      }),
      catchError(error => {
        console.error('TeamMemberService: Error updating team member:', error);
        throw error;
      })
    );
  }

  /**
   * Update an existing team member (Farmacia users)
   */
  updateTeamMemberFarmacia(id: string, teamMember: TeamMemberFarmaciaUpdateRequest): Observable<TeamMemberDetail> {
    const url = `${this.baseUrl}/${id}`;
    //console.log('TeamMemberService: Updating team member (Farmacia):', url, teamMember);

    return this.http.put<TeamMemberDetail>(url, teamMember).pipe(
      map(response => {
        //console.log('TeamMemberService: Team member updated:', response);
        return response;
      }),
      catchError(error => {
        console.error('TeamMemberService: Error updating team member:', error);
        throw error;
      })
    );
  }

  /**
   * Get picklist mappings (optional)
   */
  getPicklistMappings(): Observable<any> {
    const url = `${this.baseUrl}/picklist-mappings`;
    //console.log('TeamMemberService: Fetching picklist mappings:', url);

    return this.http.get<any>(url).pipe(
      map(response => {
        //console.log('TeamMemberService: Picklist mappings received:', response);
        return response;
      }),
      catchError(error => {
        console.error('TeamMemberService: Error fetching picklist mappings:', error);
        return of({});
      })
    );
  }

  /**
   * Build HTTP params from search criteria
   */
  private buildSearchParams(criteria: TeamMemberSearchCriteria): HttpParams {
    let params = new HttpParams();

    // Optional parameters
    if (criteria.whereField) {
      params = params.set('whereField', criteria.whereField);
    }
    if (criteria.whereValue) {
      params = params.set('whereValue', criteria.whereValue);
    }
    if (criteria.orderBy) {
      params = params.set('orderBy', criteria.orderBy);
    }
    if (criteria.orderDirection) {
      params = params.set('orderDirection', criteria.orderDirection);
    }
    if (criteria.skip !== undefined) {
      params = params.set('skip', criteria.skip.toString());
    }
    if (criteria.take !== undefined) {
      params = params.set('take', criteria.take.toString());
    }
    if (criteria.includePicklistLabels !== undefined) {
      params = params.set('includePicklistLabels', criteria.includePicklistLabels.toString());
    }
    if (criteria.includeDeactivated) {
      params = params.set('includeDeactivated', 'true');
    }

    return params;
  }
}

